import { useEffect, useState } from "react";
import { playPop, playShimmer } from "../lib/popSound";

/** Phase OO — Landing page intro animation.
 *
 *  Sequence (auto-plays on mount, ~7 seconds total):
 *    0.0s → Two balloons float up from off-screen (red + white)
 *    1.6s → Red balloon pops, reveals "pop-culture" in 3D bubble-gum font
 *    3.1s → White balloon pops, reveals "music and fashion"
 *    4.6s → Screen fades to black for 1s
 *    5.6s → "Pop Yo Shit" appears, slowly grows in bubble-gum font
 *    7.5s → Intro fades out, revealing the underlying landing page
 *
 *  Skip: tapping anywhere skips to the end. Plays once per browser tab
 *  (localStorage flag) so returning visitors aren't forced through it.
 */
const STAGE_TIMINGS = {
  popRed: 1600,
  popWhite: 3100,
  fadeBlack: 4600,
  popYoShit: 5600,
  done: 7500,
};

const FONT_3D_BUBBLEGUM = {
  fontFamily: "'Bagel Fat One', 'Lilita One', 'Fredoka', 'Comic Sans MS', cursive",
  fontWeight: 900,
  letterSpacing: "0.02em",
  textShadow: [
    "1px 1px 0 #ff80b8",
    "2px 2px 0 #ff66a8",
    "3px 3px 0 #ff4d97",
    "4px 4px 0 #ff3387",
    "5px 5px 0 #ff1a77",
    "6px 6px 0 #e60068",
    "7px 7px 0 #c0005a",
    "8px 8px 0 #9b004b",
    "9px 9px 0 #76003d",
    "12px 18px 24px rgba(0,0,0,0.4)",
  ].join(", "),
};

export default function LandingIntro({ onDone }) {
  const [stage, setStage] = useState("rising"); // rising | redPopped | whitePopped | dark | reveal | done

  useEffect(() => {
    const timers = [];
    timers.push(setTimeout(() => {
      setStage("redPopped");
      playPop({ pitch: 220, volume: 0.4 });          // red balloon — slightly higher
    }, STAGE_TIMINGS.popRed));
    timers.push(setTimeout(() => {
      setStage("whitePopped");
      playPop({ pitch: 170, volume: 0.4 });          // white balloon — slightly lower
    }, STAGE_TIMINGS.popWhite));
    timers.push(setTimeout(() => setStage("dark"), STAGE_TIMINGS.fadeBlack));
    timers.push(setTimeout(() => {
      setStage("reveal");
      playShimmer({ volume: 0.18 });                  // PYS title shimmer
    }, STAGE_TIMINGS.popYoShit));
    timers.push(setTimeout(() => {
      setStage("done");
      onDone?.();
    }, STAGE_TIMINGS.done));
    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  const skip = () => {
    setStage("done");
    onDone?.();
  };

  if (stage === "done") return null;

  return (
    <div
      data-testid="landing-intro"
      onClick={skip}
      className="fixed inset-0 z-[100] overflow-hidden cursor-pointer"
      style={{
        background:
          stage === "dark" || stage === "reveal"
            ? "#0a0510"
            : "linear-gradient(180deg, #6cb8ff 0%, #8fcaff 50%, #b8dcff 100%)",
        transition: "background 0.7s ease-in-out",
      }}
    >
      {/* Skip hint */}
      <button
        onClick={skip}
        data-testid="intro-skip-btn"
        className="absolute top-4 right-4 z-50 px-3 py-1.5 rounded-full bg-white/15 text-white text-xs font-semibold backdrop-blur hover:bg-white/30 transition-colors"
      >
        Skip →
      </button>

      {/* Sun glow — faint warm halo top-right, fades with clouds when we
          transition to the dark "Pop Yo Shit" reveal. Mirrors the Landing
          page glow so the splash → home flow feels continuous. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -right-32 h-[480px] w-[480px] rounded-full transition-opacity duration-700"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,210,0.55) 0%, rgba(255,255,210,0) 65%)",
          filter: "blur(8px)",
          opacity:
            stage === "rising" || stage === "redPopped" || stage === "whitePopped"
              ? 1
              : 0,
        }}
      />

      {/* Slow-drifting clouds (hidden once we fade to black so they don't
          peek through under "Pop Yo Shit"). Carries the brand cloud motif
          from the PWA icon into the intro. */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-700"
        style={{
          opacity: stage === "rising" || stage === "redPopped" || stage === "whitePopped" ? 1 : 0,
        }}
        aria-hidden="true"
      >
        <div className="intro-cloud intro-cloud-a" style={{ top: "12%", left: "-12%", width: "260px" }}>
          <IntroCloud opacity={0.85} />
        </div>
        <div className="intro-cloud intro-cloud-b" style={{ top: "28%", left: "62%", width: "220px" }}>
          <IntroCloud opacity={0.75} scale={0.9} />
        </div>
        <div className="intro-cloud intro-cloud-c" style={{ top: "8%", left: "70%", width: "180px" }}>
          <IntroCloud opacity={0.7} scale={0.8} />
        </div>
        <div className="intro-cloud intro-cloud-d" style={{ top: "44%", left: "5%", width: "200px" }}>
          <IntroCloud opacity={0.55} scale={0.85} />
        </div>
      </div>

      {/* RED balloon */}
      <div className="absolute left-[38%] -translate-x-1/2 bottom-0 w-44 sm:w-56">
        {/* Balloon — hidden once popped */}
        <div
          data-testid="balloon-red"
          className={stage === "rising" ? "balloon-rise" : ""}
          style={{
            opacity: stage === "rising" ? 1 : 0,
            transform: stage === "rising" ? undefined : "scale(1.6)",
            transition: "opacity 0.18s ease-out, transform 0.25s ease-out",
          }}
        >
          <Balloon color="red" />
        </div>
      </div>

      {/* WHITE balloon */}
      <div className="absolute left-[62%] -translate-x-1/2 bottom-0 w-44 sm:w-56">
        <div
          data-testid="balloon-white"
          className={
            stage === "rising" || stage === "redPopped" ? "balloon-rise balloon-rise-delay" : ""
          }
          style={{
            opacity: stage === "rising" || stage === "redPopped" ? 1 : 0,
            transform:
              stage === "rising" || stage === "redPopped" ? undefined : "scale(1.6)",
            transition: "opacity 0.18s ease-out, transform 0.25s ease-out",
          }}
        >
          <Balloon color="white" />
        </div>
      </div>

      {/* Reveal texts — viewport-centered, stacked vertically, lifted out of
          the transformed balloon containers so they're truly anchored to the
          viewport center. */}
      <div
        data-testid="reveal-red"
        className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none"
        style={{
          ...FONT_3D_BUBBLEGUM,
          fontSize: "clamp(2rem, 5vw, 3.5rem)",
          color: "#ff2a8a",
          bottom: "260px",
          opacity: stage === "redPopped" || stage === "whitePopped" ? 1 : 0,
          transform:
            stage === "redPopped" || stage === "whitePopped"
              ? "translateX(-50%) scale(1) translateY(0)"
              : "translateX(-50%) scale(0.5) translateY(20px)",
          transition: "opacity 0.4s ease-out, transform 0.5s cubic-bezier(.34,1.56,.64,1)",
        }}
      >
        pop-culture
      </div>
      <div
        data-testid="reveal-white"
        className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none"
        style={{
          ...FONT_3D_BUBBLEGUM,
          fontSize: "clamp(1.6rem, 4.5vw, 3rem)",
          color: "#ffffff",
          bottom: "180px",
          opacity: stage === "whitePopped" ? 1 : 0,
          transform: stage === "whitePopped"
            ? "translateX(-50%) scale(1) translateY(0)"
            : "translateX(-50%) scale(0.5) translateY(20px)",
          transition: "opacity 0.4s ease-out, transform 0.5s cubic-bezier(.34,1.56,.64,1)",
          textShadow:
            [
              "1px 1px 0 rgba(0,0,0,0.5)",
              "2px 2px 0 rgba(0,0,0,0.45)",
              "3px 3px 0 rgba(0,0,0,0.4)",
              "4px 4px 0 rgba(0,0,0,0.35)",
              "5px 5px 0 rgba(0,0,0,0.3)",
              "12px 18px 24px rgba(0,0,0,0.4)",
            ].join(", "),
        }}
      >
        music and fashion
      </div>

      {/* Pop bursts */}
      {stage === "redPopped" && <PopBurst x="38%" color="#ff2a8a" />}
      {(stage === "whitePopped" || stage === "dark") && (
        <PopBurst x="62%" color="#ffffff" delay />
      )}

      {/* "Pop Yo Shit" reveal */}
      {stage === "reveal" && (
        <div
          data-testid="reveal-pys-title"
          className="absolute inset-0 grid place-items-center pointer-events-none"
        >
          <h1
            className="reveal-grow text-center select-none"
            style={{
              ...FONT_3D_BUBBLEGUM,
              fontSize: "clamp(3rem, 12vw, 9rem)",
              color: "#ff2a8a",
              lineHeight: 1.05,
            }}
          >
            Pop Yo Shit
          </h1>
        </div>
      )}

      {/* fade-to-done overlay */}
      <div
        className="absolute inset-0 bg-black pointer-events-none transition-opacity duration-700"
        style={{
          opacity: stage === "done" ? 1 : 0,
        }}
      />

      <style>{`
        @keyframes balloonRise {
          0%   { transform: translateY(60vh) rotate(-6deg); }
          70%  { transform: translateY(8vh)  rotate(6deg); }
          100% { transform: translateY(0vh)  rotate(-3deg); }
        }
        .balloon-rise {
          animation: balloonRise 1.5s cubic-bezier(.25,.8,.4,1) forwards;
        }
        .balloon-rise-delay { animation-delay: 0.3s; animation-fill-mode: both; }

        @keyframes popBurst {
          0%   { transform: scale(0) rotate(0deg); opacity: 1; }
          60%  { opacity: 1; }
          100% { transform: scale(2.4) rotate(180deg); opacity: 0; }
        }
        .pop-burst {
          animation: popBurst 0.5s ease-out forwards;
        }

        @keyframes revealGrow {
          0%   { transform: scale(0.2); opacity: 0; filter: blur(20px); }
          50%  { opacity: 1; filter: blur(2px); }
          100% { transform: scale(1);   opacity: 1; filter: blur(0); }
        }
        .reveal-grow {
          animation: revealGrow 1.6s cubic-bezier(.34,1.56,.64,1) forwards;
        }

        @media (prefers-reduced-motion: reduce) {
          .balloon-rise, .pop-burst, .reveal-grow,
          .intro-cloud-a, .intro-cloud-b, .intro-cloud-c, .intro-cloud-d {
            animation: none;
          }
        }

        @keyframes introDrift {
          0%   { transform: translateX(0); }
          100% { transform: translateX(40vw); }
        }
        .intro-cloud { position: absolute; will-change: transform; }
        .intro-cloud-a { animation: introDrift 70s linear infinite; }
        .intro-cloud-b { animation: introDrift 55s linear infinite; animation-delay: -18s; }
        .intro-cloud-c { animation: introDrift 90s linear infinite; animation-delay: -30s; }
        .intro-cloud-d { animation: introDrift 80s linear infinite; animation-delay: -45s; }
      `}</style>
    </div>
  );
}

function IntroCloud({ opacity = 0.85, scale = 1 }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full" style={{ opacity, transform: `scale(${scale})` }} aria-hidden="true">
      <defs>
        <radialGradient id="introCloudShade" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="100%" stopColor="#dceeff" stopOpacity="0.95" />
        </radialGradient>
      </defs>
      <g fill="url(#introCloudShade)">
        <ellipse cx="60" cy="78" rx="50" ry="32" />
        <ellipse cx="100" cy="60" rx="42" ry="36" />
        <ellipse cx="138" cy="74" rx="38" ry="28" />
        <ellipse cx="85" cy="50" rx="28" ry="22" />
        <ellipse cx="115" cy="46" rx="22" ry="20" />
      </g>
    </svg>
  );
}

function Balloon({ color }) {
  const fill = color === "red" ? "#e6002b" : "#ffffff";
  const shadow = color === "red" ? "#a8001f" : "#dcdcdc";
  const highlight = color === "red" ? "#ff8a8a" : "#ffffff";
  return (
    <svg viewBox="0 0 200 280" className="w-full">
      <defs>
        <radialGradient id={`bg-${color}`} cx="35%" cy="30%" r="60%">
          <stop offset="0%" stopColor={highlight} stopOpacity={color === "red" ? "1" : "1"} />
          <stop offset="55%" stopColor={fill} />
          <stop offset="100%" stopColor={shadow} />
        </radialGradient>
      </defs>
      {/* Body */}
      <ellipse cx="100" cy="105" rx="76" ry="92" fill={`url(#bg-${color})`} />
      {/* Tie knot */}
      <path
        d="M88 196 L100 210 L112 196 L106 200 L100 198 L94 200 Z"
        fill={shadow}
      />
      {/* String */}
      <path
        d="M100 210 Q104 230 96 248 Q88 266 100 280"
        stroke="#222"
        strokeWidth="1.4"
        fill="none"
      />
      {/* Subtle highlight gloss */}
      <ellipse
        cx="78"
        cy="65"
        rx="16"
        ry="28"
        fill="white"
        opacity={color === "red" ? "0.35" : "0.7"}
      />
    </svg>
  );
}

function PopBurst({ x, color, delay = false }) {
  return (
    <div
      className={`pop-burst absolute bottom-[120px] -translate-x-1/2 pointer-events-none`}
      style={{ left: x, animationDelay: delay ? "0.05s" : "0s" }}
      aria-hidden="true"
    >
      <svg width="240" height="240" viewBox="-120 -120 240 240">
        {Array.from({ length: 14 }).map((_, i) => {
          const angle = (i / 14) * 360;
          const len = 80 + (i % 3) * 18;
          return (
            <line
              key={i}
              x1="0"
              y1="0"
              x2={Math.cos((angle * Math.PI) / 180) * len}
              y2={Math.sin((angle * Math.PI) / 180) * len}
              stroke={color}
              strokeWidth="6"
              strokeLinecap="round"
            />
          );
        })}
        <circle cx="0" cy="0" r="32" fill={color} opacity="0.85" />
      </svg>
    </div>
  );
}
